<script setup lang="ts">
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
// import required modules
import { Navigation, Pagination } from 'swiper/modules'
import { debounce } from 'perfect-debounce'
import { computed, onMounted, ref, watch, type PropType, type Ref } from 'vue'
import {
  listMemorialMessages,
  MSG_GIFT,
  type ListMessageResponse,
  type MemorialMessageDTO
} from '@/composables/memorize/memorize'
import MemorialGiftItem from '../MemorialGiftItem.vue'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { renderMessage } from '@/utils/util'
import { showGiftPlay } from '@/composables/memorize/gift'
import { ElMessage } from 'element-plus'
import flowerIcon from '@/assets/juhua.jpg'

const props = defineProps({
  serviceId: {
    type: String,
    required: true
  },
  canManage: {
    type: Boolean,
    required: true
  }
})

const lastId: Ref<string | null> = ref(null)
const pageSize = ref(15)
const items: Ref<Array<MemorialMessageDTO>> = ref([])

const hasMoreItems = ref(true)
const dataState = ref('none')

const giftCnt = ref(0)
const activeMenuItem = ref<string | null>(null)

const messageRequested = ref(false)

onMounted(() => {
  loadMessages(true)
})

function setActiveMenuItem(itemId: string | null) {
  activeMenuItem.value = itemId
}

async function loadMessages(isInitial: boolean = false, last: string | null = null) {
  if (dataState.value === 'loading') {
    return
  }

  if (isInitial) {
    lastId.value = null
    if (dataState.value === 'none') {
      dataState.value = 'loading'
    }
  }

  dataState.value = 'loading'

  console.log('nextMsgId:', lastId.value)

  const { data, error, statusCode } = await listMemorialMessages(props.serviceId, last, pageSize.value, MSG_GIFT)

  if (error.value) {
    console.error('err:', error)
    handleErr(data, error, statusCode)

    console.log('lastId:', last)

    if (last === null) {
      dataState.value = 'error'
    } else {
      dataState.value = 'failed'
    }
  } else {
    const resData = data as unknown as Ref<CommonResponse<ListMessageResponse>>

    if (resData.value.code === CODE_OK) {
      // success
      console.log('msgs:', resData.value.data)

      const response: ListMessageResponse = resData.value.data

      let delayTime = last === null ? 300 : 0

      setTimeout(() => {
        if (last === null) {
          items.value = []
          dataState.value = 'done'
        }

        const normalizedItems = response.items.filter((item) => item?.id && item?.gift && item?.creator)
        items.value = [...items.value, ...normalizedItems]

        giftCnt.value = response.total
        dataState.value = 'none'
        hasMoreItems.value = true

        if (response.items.length == 0) {
          hasMoreItems.value = false
        } else {
          const nextLastId = response.items[response.items.length - 1]?.id || null
          if (last !== null && nextLastId && nextLastId === last) {
            hasMoreItems.value = false
          } else {
            lastId.value = nextLastId
          }
        }

        // const foundMsg = items.value.find((item) => item.id === targetMsgId.value)
        // if (foundMsg && targetMsgId.value && !located.value) {
        //   toLocate()
        // }
      }, delayTime)
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })

      if (last === null) {
        dataState.value = 'error'
      } else {
        dataState.value = 'failed'
      }
    }
  }
  setTimeout(() => {
    messageRequested.value = true
  }, 300)
}

const allGifts = computed(() => {
  return items.value
})

let innerWidth = ref(0)
if (import.meta.client) {
  innerWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', (e) => debounced())
})

const debounced = debounce(() => {
  innerWidth.value = window.innerWidth
}, 100)

const modules = computed(() => {
  return innerWidth.value > 768 ? [Navigation, Pagination] : groupedGifts.value.length <= 100 ? [Pagination] : []
})

const groupedGifts = computed(() => {
  const result: MemorialMessageDTO[][] = []
  if (allGifts.value && allGifts.value.length > 0) {
    const itemsPerPage = innerWidth.value < 768 ? 3 : 5
    for (let i = 0; i < allGifts.value.length; i += itemsPerPage) {
      result.push(allGifts.value.slice(i, i + itemsPerPage))
    }
  }
  return result
})

function onReachEnd() {
  if (hasMoreItems.value) {
    loadMessages(false, lastId.value)
  }
}

const emit = defineEmits(['locate', 'onShowGift'])

watch(
  () => showGiftPlay.value,
  (newVal) => {
    if (!newVal) {
      loadMessages(true)
      emit('locate')
    }
  }
)

const rootElement = ref<HTMLElement | null>(null)

defineExpose({
  locate: () => {
    if (rootElement.value) {
      const rect = rootElement.value.getBoundingClientRect()
      console.log('GiftShowSlide position:', rect)
      return rect
    }
    return null
  }
})

function handleDelete(msgId: string) {
  items.value = items.value.filter((item) => item.id !== msgId)

  if (items.value.length > 0) {
    lastId.value = items.value[items.value.length - 1].id
  } else {
    lastId.value = null
  }
  giftCnt.value = giftCnt.value - 1
}

const swiperOptions = {
  navigation: (width: number) => (width > 768 ? { enabled: true } : undefined) as any,
  pagination: { enabled: true, clickable: true } as any
}
</script>

<template>
  <div ref="rootElement" w-full flex="~ col" px-1>
    <div flex="~ row" mt-4 mb-2 items-center justify-between text-theme-dark>
      <h2 text-xl font-semibold>{{ $t('m.title_gift', { count: giftCnt }) }}</h2>
    </div>
    <div
      v-if="messageRequested && (items == null || items.length == 0)"
      flex="~ row"
      items-center
      justify-center
      my-2
      min-h-40
      cursor-pointer>
      <div
        p-2
        px-4
        rounded-full
        border-1
        border-theme-main
        hover:border-theme-main
        text-theme-main
        flex="~ row"
        items-center
        justify-center
        @click="$emit('onShowGift')"
        opacity-85
        text-sm>
        {{ $t('empty.send_flower') }}
      </div>
    </div>
    <ClientOnly>
      <swiper
        :slides-per-view="1"
        :space-between="30"
        :navigation="swiperOptions.navigation(innerWidth)"
        :pagination="swiperOptions.pagination"
        :modules="modules"
        @reachEnd="onReachEnd"
        w-full>
        <swiper-slide w-auto v-for="(slide, slideIndex) in groupedGifts" :key="slideIndex">
          <div
            w-full
            grid
            grid-cols-3
            md:grid-cols-5
            :class="{
              'pb-6': groupedGifts.length <= 20,
              'pb-12': groupedGifts.length > 20 && groupedGifts.length <= 40,
              'pb-18': groupedGifts.length > 40 && groupedGifts.length <= 60,
              'pb-24': groupedGifts.length > 60 && groupedGifts.length <= 80,
              'pb-30': groupedGifts.length > 80 && groupedGifts.length <= 100
            }"
            gap-1
            items-start
            justify-center>
            <MemorialGiftItem
              v-for="(item, index) in slide"
              :key="index"
              :item="item"
              :canManage="props.canManage"
              :activeMenuItem="activeMenuItem"
              @onDelete="handleDelete"
              @setActiveMenuItem="setActiveMenuItem"
              showGroup />
          </div>
        </swiper-slide>
      </swiper>
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
/* style */
</style>
